import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  MessageCircle,
  NotebookPen,
  BookOpen,
  TrendingUp,
  Menu,
  LogOut,
  ShieldAlert,
  Heart,
  Swords,
  Wind,
  RotateCcw,
  Users,
  Search,
  Trophy,
  Settings as SettingsIcon,
  Smile,
  Crown,
  Scroll,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import PaywallModal from "@/components/PaywallModal";
import NotificationBell from "@/components/NotificationBell";
import api from "@/lib/api";
import useNoIndex from "@/hooks/useNoIndex";

const bottomNav = [
  { to: "/app", icon: Home, label: "Home" },
  { to: "/app/coach", icon: MessageCircle, label: "Coach" },
  { to: "/app/unload", icon: NotebookPen, label: "Unload" },
  { to: "/app/word", icon: BookOpen, label: "Word" },
  { to: "/app/progress", icon: TrendingUp, label: "Progress" },
];

const menuItems = [
  { to: "/app/settings", icon: SettingsIcon, label: "Settings" },
  { to: "/app/checkin", icon: Smile, label: "Daily Check-In" },
  { to: "/app/growth-plan", icon: Sparkles, label: "Growth Plan", plus: true },
  { to: "/app/groups", icon: UsersRound, label: "Accountability Groups", plus: true },
  { to: "/app/legacy", icon: Scroll, label: "Legacy Tracker", plus: true },
  { to: "/app/maskoff", icon: Swords, label: "Mask Off Mode" },
  { to: "/app/before-react", icon: Wind, label: "Before You React" },
  { to: "/app/reset", icon: RotateCcw, label: "Frustration Reset" },
  { to: "/app/brothers", icon: Heart, label: "Brother Check" },
  { to: "/app/community", icon: Users, label: "Brotherhood" },
  { to: "/app/bible", icon: Search, label: "Bible Search" },
  { to: "/app/challenges", icon: Trophy, label: "Challenges" },
];

export default function Layout() {
  useNoIndex();
  const { user, logout } = useAuth();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.post("/activity/ping").catch(() => {});
  }, [location.pathname]);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <div className="min-h-screen bg-background text-foreground grain relative">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 pt-safe">
        <div className="max-w-2xl mx-auto px-4 safe-x h-14 flex items-center justify-between">
          <button
            data-testid="logo-home-btn"
            onClick={() => navigate("/app")}
            className="heading text-2xl font-black tracking-tighter"
          >
            UN<span className="text-primary">LOAD</span>DAILY
          </button>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              data-testid="header-plus-btn"
              onClick={() => navigate("/app/subscribe")}
              className={`h-9 px-3 grid place-items-center rounded-lg border transition-colors text-xs font-bold uppercase tracking-wider flex-row gap-1 ${
                isPremium
                  ? "border-accent/50 text-accent"
                  : "border-primary/50 text-primary hover:bg-primary/10"
              }`}
            >
              <span className="flex items-center gap-1">
                <Crown size={14} /> {isPremium ? "Plus" : "Go Plus"}
              </span>
            </button>
            <button
              data-testid="emergency-header-btn"
              onClick={() => navigate("/app/emergency")}
              className="h-9 w-9 grid place-items-center rounded-lg border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors"
              aria-label="Emergency support"
            >
              <ShieldAlert size={18} />
            </button>
            <button
              data-testid="settings-header-btn"
              onClick={() => navigate("/app/settings")}
              className="h-9 w-9 grid place-items-center rounded-lg border border-white/15 hover:border-white/40 transition-colors"
              aria-label="Settings"
            >
              <SettingsIcon size={18} />
            </button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="menu-btn"
                  className="h-9 w-9 grid place-items-center rounded-lg border border-white/15 hover:border-white/40 transition-colors"
                  aria-label="Menu"
                >
                  <Menu size={18} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0d0d0d] border-white/10 w-[300px] p-0">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <div className="p-6 border-b border-white/10">
                  <p className="text-xs text-muted-foreground tracking-widest uppercase">Signed in as</p>
                  <p className="heading text-2xl font-bold mt-1">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
                <nav className="p-3 flex flex-col">
                  {menuItems.map((m) => (
                    <button
                      key={m.to}
                      data-testid={`menu-link-${m.to.split("/").pop()}`}
                      onClick={() => go(m.to)}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/5 transition-colors text-left"
                    >
                      <m.icon size={18} className="text-primary" />
                      <span className="font-medium flex-1">{m.label}</span>
                      {m.plus && !isPremium && (
                        <Crown size={14} className="text-primary" />
                      )}
                    </button>
                  ))}
                  <button
                    data-testid="logout-btn"
                    onClick={async () => {
                      await logout();
                      navigate("/app/login");
                    }}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-destructive/10 text-destructive transition-colors text-left mt-2"
                  >
                    <LogOut size={18} />
                    <span className="font-medium">Log Out</span>
                  </button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="max-w-2xl mx-auto px-4 pt-5 pb-28 relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      <PaywallModal />

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-black/80 border-t border-white/10 pb-safe">
        <div className="max-w-2xl mx-auto flex justify-around py-2">
          {bottomNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/app"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                  <span className="text-[10px] font-semibold tracking-wide uppercase">{n.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
